<template>
  <article
    ref="mainCard"
    :class="[
      'main-card',
      `main-card--${element}`,
      { 'main-card--expand': isExpanded, 'main-card--small': small },
    ]"
  >
    <div :class="[
      'card-hint',
      'card-hint__left',
      { 'card-hint__visible': activeIndex > 0 && activeDataList.length > 1 }
    ]"></div>
    <div :class="[
      'card-hint',
      'card-hint__right',
      { 'card-hint__visible': activeIndex < activeDataList.length - 1 && activeDataList.length > 1 }
    ]"></div>

    <h3 v-if="title" class="main-card__title" v-html="activeCard.name"></h3>

    <div class="main-card__controls">
      <SvgInfo
        v-if="tooltip"
        v-tooltip.useWindowLimits="tooltip"
        :data-tooltip-style="tooltipStyleForDevice"
      />

      <SvgExpand
        v-if="!isExpanded"
        @click="toggleExpand"
      />

      <SvgClose
        v-else
        class="main-card__svg-x-circle"
        @click="toggleExpand"
      />

      <SvgDelete
        v-if="!isExpanded"
        class="main-card__svg-delete"
        @click="$emit('delete')"
      />
    </div>

    <div
      v-if="enableScroll"
      class="scrollable-content-wrapper"
    >
      <div class="scroll-effect-top-bottom" :class="{
        'hide-top-scroll-effect': hideTopScrollEffect,
        'hide-bottom-scroll-effect': hideBottomScrollEffect,
      }">
        <div class="scroll-effect-left-right" :class="{
          'hide-left-scroll-effect': hideLeftScrollEffect,
          'hide-right-scroll-effect': hideRightScrollEffect,
        }">
          <div ref="innerScrollWrapper" class="inner-scroll-wrapper" @scroll.passive="updateContainerScroll($event)">
            <slot name="card-slot" :isExpanded="!isExpanded" />
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="main-card--noscroll"
    >
      <slot name="card-slot" :isExpanded="!isExpanded" />
    </div>
  </article>
</template>

<script>
import { mapGetters } from 'vuex';
import scrollShadowMixin from '@/mixins/scrollShadowMixin';
import eventHub from '../eventHub';

export default {
  name: 'Card',

  mixins: [scrollShadowMixin],

  components: {
    SvgInfo: () => import('@/assets/svgs/info.svg?inlne'),
    SvgExpand: () => import('@/assets/svgs/expand.svg?inline'),
    SvgDelete: () => import('@/assets/svgs/delete.svg?inline'),
    SvgClose: () => import('@/assets/svgs/close.svg?inline'),
  },

  props: {
    title: {
      type: String,
      default: '',
    },
    small: {
      type: Boolean,
      default: false,
    },
    element: {
      type: String,
      default: '',
    },
    enableScroll: {
      type: Boolean,
      default: true,
    },
    tooltip: {
      type: String,
    },
    tooltipStyle: {
      type: Object,
    },
    hideLeftScrollEffect: {
      type: Boolean,
      default: false,
    },
    hideRightScrollEffect: {
      type: Boolean,
      default: false,
    },
    hideTopScrollEffect: {
      type: Boolean,
      default: false,
    },
    hideBottomScrollEffect: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    isExpanded: false,
  }),

  mounted() {
    this.$emit('action', { index: -1, label: '' });

    this.setupResizeObserver(this.$refs.innerScrollWrapper);

    this.resetScroll(document.body);
    this.forceContentRedraw(this.$refs.mainCard, this.$refs.innerScrollWrapper);
  },

  unmounted() {
    this.tearDownResizeObserver(this.$refs.innerScrollWrapper);
  },

  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded;

      this.resetScroll(document.body);
      this.forceContentRedraw(this.$refs.mainCard, this.$refs.innerScrollWrapper);
    },

    updateContainerScroll($event) {
      eventHub.$emit('hide-tooltip');
      this.updateScrollEffects($event);
    },
  },

  computed: {
    ...mapGetters([
      'activeCard',
      'activeIndex',
      'activeDataList'
    ]),

    tooltipStyleForDevice() {
      return this.tooltipStyle?.[this.$mq];
    },
  }
};
</script>

<style lang="scss" scoped>
.main-card {
  background: $white;
  padding: rem(20);
  height: 100%;
  width: 100%;
  position: relative;
  border: 1px solid #707070;

  &--light {
    border-color: #ffce00;
  }

  &--medium {
    border-color: #be5b92;
  }

  &--dark {
    border-color: #006d8a;
  }

  &--noscroll {
    height: calc(100% - 31px);

    & > * {
      height: 100%;
    }
  }

  &__title {
    color: $cardTitle;
    font-size: rem(25);
    line-height: 1.25;
    margin-bottom: 0;

    @include iPadProStyles {
      font-size: rem(26);
    }
  }

  &__controls {
    position: absolute;
    right: rem(20);
    top: rem(20);
    z-index: 10;

    svg {
      width: rem(22);
      cursor: pointer;

      &:not(:last-child) {
        margin-right: rem(10);
      }
    }
  }

  &--expand {
    position: fixed;
    width: 95vw;
    height: 95vh;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    box-shadow: 0 0 118px -12px rgba(0, 0, 0, 0.75);
    z-index: 99;
    border-radius: rem(7);

    .card-hint {
      display: none !important;
    }
  }

  &--small {
    padding: rem(20) rem(18);

    .main-card__svg-delete {
      display: none;
    }

    .main-card__controls {
      right: rem(-5);
      top: rem(-2);

      svg {
        width: rem(12);
      }
    }
  }
  &__tool-tip {
    position: absolute;
    width: 300px;
    background-color: white;
    border: 3px #0092A7 solid;
    border-radius: 10px;
    padding: 20px;
    left: -280px;
    top: 40px;
    z-index: 9;
  }
}

.card-hint {
  position: absolute;
  top: -1px;
  width: rem(20);
  height: calc(100% + 2px);
  background: #ffffff;
  border: 1px solid #707070;
  display: none;

  &__left {
    left: rem(-46);
    border-left: none;
  }

  &__right {
    right: rem(-46);
    border-right: none;
  }

  &__visible {
    display: block;
  }
}

 /**
  * The `!important` flags belows are necessarily because of the overuse of `Card` in the
  * MOA screen, which uses the mixin `text-ellipsis` that sets `display` to `-webkit-box`
  * and messes up with the logic. This needs to be fixed by not using `Card` in the MOA
  * screen, a refactor that we'll do later. For now we're trying to live with this tech debt.
  */
.expand-show {
  display: none !important;

  .main-card--expand & {
    display: block !important;
  }
}

.scrollable-content-wrapper {
  @include scrollable-with-shadow();
  height: calc(100% - 30px);
}

.inner-scroll-wrapper {
  display: flex;
  overflow: auto;
  width: 100%;
  height: 100%;

  > * {
    width: 100%;
    display: flex;
    flex-direction: column;
  }
}

</style>
